import {
  faArrowRight,
  faArrowsRotate,
  faCircleInfo,
  faListOl,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import Alert from 'src/components/ui/Alert';
import Button from 'src/components/ui/Button';
import Checkbox from 'src/components/ui/Checkbox';
import EmptyState from 'src/components/ui/EmptyState';
import SectionLabel from 'src/components/ui/SectionLabel';
import StatusBadge from 'src/components/ui/StatusBadge';
import {
  getStepCount,
  sortMaintenanceRequirements,
  summarizeFrequency,
} from 'src/utils/maintenance';

import MaintenanceDueStatusBadge from './MaintenanceDueStatusBadge';

const RequirementRow = ({ requirement, checked, onToggle, maintenanceDue }) => (
  <div
    className={`mnt-select-row mnt-status mnt-status--${maintenanceDue.status_indicator}`}
    role='button'
    tabIndex={0}
    onClick={() => onToggle(requirement.uri)}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') onToggle(requirement.uri);
    }}
  >
    <span onClick={event => event.stopPropagation()}>
      <Checkbox
        id={`mnt-sel-${requirement.uri}`}
        checked={checked}
        onChange={() => onToggle(requirement.uri)}
      />
    </span>
    <span className='mnt-select-row__icon mnt-status-bg'>
      <FontAwesomeIcon icon={faWrench} />
    </span>
    <div className='mnt-select-row__body'>
      <div>
        <div className='mnt-select-row__name'>{requirement.name}</div>
        <div className='mnt-select-row__meta'>
          <StatusBadge size='sm' uppercase={false} icon={faArrowsRotate}>
            {summarizeFrequency(requirement)}
          </StatusBadge>
          <StatusBadge size='sm' uppercase={false} icon={faListOl}>
            {getStepCount(requirement)} steps
          </StatusBadge>
        </div>
      </div>
      <MaintenanceDueStatusBadge
        status={maintenanceDue.status_indicator}
        className='align-self-start'
      />
    </div>
  </div>
);

RequirementRow.propTypes = {
  requirement: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  maintenanceDue: {
    // eslint-disable-next-line camelcase
    status_indicator: PropTypes.string,
  },
};

const Group = ({
  icon,
  label,
  requirements,
  selected,
  onToggle,
  onToggleGroup,
  maintenanceDueByRequirement,
}) => {
  if (!requirements.length) return null;
  const groupUris = requirements.map(requirement => requirement.uri);
  const allSelected = groupUris.every(uri => selected.includes(uri));

  return (
    <>
      <SectionLabel
        icon={icon}
        action={
          <button
            type='button'
            className='section-label__action'
            onClick={() => onToggleGroup(groupUris)}
          >
            {allSelected ? 'Uncheck all' : 'Select all'}
          </button>
        }
      >
        {label}
      </SectionLabel>
      <div className='mnt-group-card'>
        {requirements.map(requirement => (
          <RequirementRow
            key={requirement.uri}
            requirement={requirement}
            checked={selected.includes(requirement.uri)}
            maintenanceDue={maintenanceDueByRequirement[requirement.uri]}
            onToggle={onToggle}
          />
        ))}
      </div>
    </>
  );
};

Group.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  label: PropTypes.string.isRequired,
  requirements: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  maintenanceDueByRequirement: PropTypes.array.isRequired,
};

/** Select screen — choose which requirements to fulfill, grouped by scope. */
const RequirementSelect = ({
  anchor,
  instanceReqs,
  typeReqs,
  selected,
  onToggle,
  onToggleGroup,
  onCancel,
  onContinue,
  maintenanceDueForEquipment,
}) => {
  const maintenanceDueByRequirement = maintenanceDueForEquipment.reduce((lookup, item) => {
    lookup[item.maintenance_requirement] = item;
    return lookup;
  }, {});

  const sortedInstanceReqs = sortMaintenanceRequirements(instanceReqs, maintenanceDueByRequirement);
  const sortedTypeReqs = sortMaintenanceRequirements(typeReqs, maintenanceDueByRequirement);

  return (
    <div className='mnt-enter'>
      <Alert variant='info' icon={faCircleInfo} title='Choose what to do today'>
        Pick the requirements you’ll fulfill. Their checklists combine into your to-do list.
      </Alert>

      <Group
        icon={anchor.instanceIcon}
        label={anchor.instanceLabel}
        requirements={sortedInstanceReqs}
        selected={selected}
        maintenanceDueByRequirement={maintenanceDueByRequirement}
        onToggle={onToggle}
        onToggleGroup={onToggleGroup}
      />
      <Group
        icon={anchor.typeIcon}
        label={anchor.typeLabel}
        requirements={sortedTypeReqs}
        selected={selected}
        maintenanceDueByRequirement={maintenanceDueByRequirement}
        onToggle={onToggle}
        onToggleGroup={onToggleGroup}
      />

      {!instanceReqs.length && !typeReqs.length && (
        <EmptyState variant='note'>
          No maintenance requirements are configured for this equipment yet.
        </EmptyState>
      )}

      <div className='mnt-footbar'>
        <Button variant='default' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          fill
          variant='primary'
          disabled={!selected.length}
          badge={selected.length}
          icon={<FontAwesomeIcon icon={faArrowRight} />}
          iconPosition='right'
          onClick={onContinue}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

RequirementSelect.propTypes = {
  anchor: PropTypes.object.isRequired,
  instanceReqs: PropTypes.array.isRequired,
  typeReqs: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  maintenanceDueForEquipment: PropTypes.array.isRequired,
};

export default RequirementSelect;
